import { useEffect, useRef, useState } from 'react'
import type { ConnectIdleAuthPhase } from './ConnectIdleShell'

/**
 * Progress ring drawn around the floating Connect pill while the widget is not usable yet:
 *   electing tab  → indeterminate spin ("Connecting to Amazon Connect…")
 *   master, CCP initializing → 1/3, restoring session → 2/3
 *   agent ready   → fills to 100% and fades out
 * Hidden for observer tabs and for states that need the interpreter (login, auth errors) —
 * the panel itself explains those.
 */

export type PillRingState =
    | { mode: 'hidden' }
    | { mode: 'indeterminate'; label: string }
    | { mode: 'determinate'; progress: number; label: string; complete?: boolean }

const COMPLETE_HOLD_MS = 700

const BOOT_STEPS: Partial<Record<ConnectIdleAuthPhase, { progress: number; label: string }>> = {
    initializing: { progress: 1 / 3, label: 'Starting Amazon Connect…' },
    restoring: { progress: 2 / 3, label: 'Restoring your Connect session…' },
}

export function usePillRingState(params: {
    isMasterTab: boolean | null
    bootPhase: ConnectIdleAuthPhase
}): PillRingState {
    const { isMasterTab, bootPhase } = params
    const wasBootingRef = useRef(false)
    const [showComplete, setShowComplete] = useState(false)

    const step = isMasterTab === true ? BOOT_STEPS[bootPhase] : undefined
    const isBooting = isMasterTab === null || !!step

    // Ring was visible and the agent is now ready → play the "fill to 100%" beat once.
    useEffect(() => {
        if (isBooting) {
            wasBootingRef.current = true
            setShowComplete(false)
            return
        }
        if (!wasBootingRef.current) return
        wasBootingRef.current = false
        if (isMasterTab !== true || bootPhase !== 'idle') return
        setShowComplete(true)
        const timer = window.setTimeout(() => setShowComplete(false), COMPLETE_HOLD_MS)
        return () => window.clearTimeout(timer)
    }, [isBooting, isMasterTab, bootPhase])

    if (isMasterTab === null) {
        return { mode: 'indeterminate', label: 'Connecting to Amazon Connect…' }
    }
    if (step) {
        return { mode: 'determinate', progress: step.progress, label: step.label }
    }
    if (showComplete) {
        return { mode: 'determinate', progress: 1, label: 'Connected to Amazon Connect', complete: true }
    }
    return { mode: 'hidden' }
}

const SIZE = 62
const STROKE = 3
const RADIUS = (SIZE - STROKE) / 2
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

export function PillProgressRing({ state }: { state: PillRingState }) {
    if (state.mode === 'hidden') return null

    const isIndeterminate = state.mode === 'indeterminate'
    const progress = isIndeterminate ? 0.28 : Math.max(0, Math.min(1, state.progress))
    const offset = CIRCUMFERENCE * (1 - progress)
    const className = [
        'cw-pill-ring',
        isIndeterminate ? 'is-indeterminate' : 'is-determinate',
        state.mode === 'determinate' && state.complete ? 'is-complete' : '',
    ]
        .filter(Boolean)
        .join(' ')

    return (
        <svg
            className={className}
            width={SIZE}
            height={SIZE}
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            aria-hidden='true'
            focusable='false'
        >
            <circle
                className='cw-pill-ring-track'
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                strokeWidth={STROKE}
                fill='none'
            />
            <circle
                className='cw-pill-ring-bar'
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                strokeWidth={STROKE}
                fill='none'
                strokeLinecap='round'
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={offset}
            />
        </svg>
    )
}
